import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Col, Card, CardBody, Button, Table } from "reactstrap"
import api from "../../services/api"
import Swal from "sweetalert2"
import AuthService from "../../services/Auth.service"
import { useNavigate } from "react-router-dom"
import SignatureCanvas from "react-signature-canvas"
import Select from "react-select"
import { TIPO_ENTREGA, formatarDataBR, selectMenuStyles } from "./osHelpers"
import {
  gerarProtocolo as gerarProtocoloLogic,
  formatarDataInput,
  validarNovoItem,
  validarFormularioOS,
  mapearStatusLabel,
} from "./osLogic"
import BotaoAcao from "./components/BotaoAcao"
import ModalFormOS from "./components/ModalFormOS"
import TipoServicoModal from "./components/TipoServicoModal"
import { gerarPdfOS } from "./osPdf"

const CriarOS = () => {
  const navigate = useNavigate()
  const user = AuthService.getCurrentUser()
  const signatureRef = useRef(null)
  const signatureContainerRef = useRef(null)
  const [canvasSize, setCanvasSize] = useState({ width: 900, height: 400 })

  // Estados do formulário
  const [protocolo, setProtocolo] = useState("")
  const [dataAbertura, setDataAbertura] = useState("")
  const [condominios, setCondominios] = useState([])
  const [condominioSelecionado, setCondominioSelecionado] = useState("")
  const [responsavelTecnico, setResponsavelTecnico] = useState("")
  const [tipoServico, setTipoServico] = useState("") // Agora será o ID do GL_TIPO
  const [tiposServico, setTiposServico] = useState([])
  const [descricao, setDescricao] = useState("")
  const [assinatura, setAssinatura] = useState("")
  const [status, setStatus] = useState("ABERTO")

  // Estados para itens
  const [itens, setItens] = useState([]) // Lista de itens adicionados
  const [novoItem, setNovoItem] = useState("") // Nome do item sendo adicionado
  const [novaQuantidade, setNovaQuantidade] = useState("") // Quantidade do item sendo adicionado

  // Modais
  const [modalAssinatura, setModalAssinatura] = useState(false)
  const [modalNovoTipo, setModalNovoTipo] = useState(false)
  const [novoTipoServico, setNovoTipoServico] = useState("")

  useEffect(() => {
    gerarProtocolo()
    setDataHoje()
    carregarCondominios()
    carregarTiposServico()
  }, [])

  // Alinha o bitmap interno do canvas com o tamanho exibido (evita offset no toque).
  useEffect(() => {
    if (!modalAssinatura) return

    const syncCanvasSize = () => {
      const container = signatureContainerRef.current
      const width = container
        ? Math.max(container.clientWidth, 280)
        : Math.min(window.innerWidth - 80, 900)
      setCanvasSize({ width, height: 400 })
    }

    const timer = setTimeout(syncCanvasSize, 80)
    window.addEventListener("resize", syncCanvasSize)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("resize", syncCanvasSize)
    }
  }, [modalAssinatura])

  const gerarProtocolo = () => {
    setProtocolo(gerarProtocoloLogic())
  }

  const setDataHoje = () => {
    setDataAbertura(formatarDataInput())
  }

  const carregarCondominios = () => {
    api
      .get("/empresa/")
      .then(response => {
        setCondominios(response.data)
      })
      .catch(error => {
        console.error("Erro ao carregar condomínios:", error)
      })
  }

  const toggleModalNovoTipo = () => {
    setModalNovoTipo(!modalNovoTipo)
    if (!modalNovoTipo) {
      setNovoTipoServico("")
    }
  }

  const salvarNovoTipo = async () => {
    if (!novoTipoServico.trim()) {
      Swal.fire({
        icon: "warning",
        text: "Digite o nome do novo tipo de serviço",
      })
      return
    }

    // Verificar se já existe
    if (
      tiposServico.some(
        t => t.NAME.toLowerCase() === novoTipoServico.trim().toLowerCase(),
      )
    ) {
      Swal.fire({
        icon: "warning",
        text: "Este tipo de serviço já existe",
      })
      return
    }

    // Criar novo tipo via API
    try {
      const response = await api.post("/gl_tipo", {
        NAME: novoTipoServico.trim(),
      })

      Swal.fire({
        icon: "success",
        title: "Tipo de serviço criado!",
        text: `"${novoTipoServico}" foi adicionado com sucesso`,
        timer: 2000,
        showConfirmButton: false,
      })

      // Recarregar lista de tipos
      await carregarTiposServico()

      // Selecionar automaticamente o tipo criado
      // A API pode retornar o ID do tipo criado de formas diferentes
      let novoTipoId
      if (response.data && response.data[0] && response.data[0][0]) {
        novoTipoId = response.data[0][0].OID_TIPO
      } else if (response.data && response.data[0]) {
        novoTipoId = response.data[0].OID_TIPO
      } else if (response.data && response.data.OID_TIPO) {
        novoTipoId = response.data.OID_TIPO
      }

      // Se não conseguiu o ID da resposta, busca pelo nome
      if (!novoTipoId) {
        const tiposAtualizados = await api.get("/gl_tipo")
        const tipoEncontrado = tiposAtualizados.data.find(
          t => t.NAME.toLowerCase() === novoTipoServico.trim().toLowerCase(),
        )
        if (tipoEncontrado) {
          novoTipoId = tipoEncontrado.OID_TIPO
        }
      }

      if (novoTipoId) {
        setTipoServico(String(novoTipoId))
      }

      // Mantém o modal aberto e limpa o campo para cadastrar o próximo tipo.
      setNovoTipoServico("")
    } catch (error) {
      console.error("Erro ao criar tipo de serviço:", error)
      Swal.fire({
        icon: "error",
        title: "Erro ao criar tipo",
        text: "Não foi possível criar o tipo de serviço. Tente novamente.",
      })
    }
  }

  const carregarTiposServico = async () => {
    try {
      const response = await api.get("/gl_tipo")
      // Ordena pelos criados mais recentes primeiro (OID_TIPO é sequencial).
      setTiposServico(
        [...response.data].sort((a, b) => b.OID_TIPO - a.OID_TIPO),
      )
    } catch (error) {
      console.error("Erro ao carregar tipos de serviço:", error)
      Swal.fire({
        icon: "error",
        title: "Erro ao carregar tipos de serviço",
        text: "Não foi possível carregar os tipos de serviço. Tente novamente.",
      })
    }
  }

  const editarTipoServico = async (oidTipo, novoNome) => {
    // Bloqueia nome duplicado (ignorando o próprio tipo em edição)
    if (
      tiposServico.some(
        t =>
          t.OID_TIPO !== oidTipo &&
          t.NAME.toLowerCase() === novoNome.toLowerCase(),
      )
    ) {
      Swal.fire({
        icon: "warning",
        text: "Já existe um tipo de serviço com esse nome",
      })
      return
    }

    try {
      await api.put(`/gl_tipo/${oidTipo}`, { NAME: novoNome })
      Swal.fire({
        icon: "success",
        title: "Tipo de serviço atualizado!",
        timer: 1500,
        showConfirmButton: false,
      })
      await carregarTiposServico()
    } catch (error) {
      console.error("Erro ao atualizar tipo de serviço:", error)
      Swal.fire({
        icon: "error",
        title: "Erro ao atualizar",
        text: "Não foi possível atualizar o tipo de serviço. Tente novamente.",
      })
    }
  }

  const excluirTipoServico = tipo => {
    Swal.fire({
      title: "Excluir tipo de serviço?",
      html: `Tem certeza que deseja excluir <strong>"${tipo.NAME}"</strong>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    }).then(async result => {
      if (!result.isConfirmed) return
      try {
        await api.delete(`/gl_tipo/${tipo.OID_TIPO}`)
        // Se o tipo excluído estava selecionado no formulário, limpa a seleção
        if (String(tipoServico) === String(tipo.OID_TIPO)) {
          setTipoServico("")
        }
        Swal.fire({
          icon: "success",
          title: "Excluído!",
          timer: 1500,
          showConfirmButton: false,
        })
        await carregarTiposServico()
      } catch (error) {
        console.error("Erro ao excluir tipo de serviço:", error)
        Swal.fire({
          icon: "error",
          title: "Erro ao excluir",
          text: "Não foi possível excluir. Pode haver Ordens de Serviço usando este tipo.",
        })
      }
    })
  }

  const toggleModalAssinatura = () => {
    setModalAssinatura(!modalAssinatura)
  }

  const limparAssinatura = () => {
    if (signatureRef.current) {
      signatureRef.current.clear()
    }
  }

  const salvarAssinatura = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const assinaturaData = signatureRef.current.toDataURL()
      setAssinatura(assinaturaData)
      toggleModalAssinatura()
      Swal.fire({
        icon: "success",
        title: "Assinatura capturada com sucesso!",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      Swal.fire({
        icon: "warning",
        text: "Por favor, assine antes de salvar",
      })
    }
  }

  const limparFormulario = () => {
    gerarProtocolo()
    setDataHoje()
    setCondominioSelecionado("")
    setResponsavelTecnico("")
    setTipoServico("")
    setDescricao("")
    setItens([])
    setNovoItem("")
    setNovaQuantidade("")
    setAssinatura("")
    setStatus("ABERTO")
  }

  // Funções para gerenciar itens
  const adicionarItem = () => {
    const resultado = validarNovoItem(novoItem, novaQuantidade)
    if (!resultado.valido) {
      Swal.fire({
        icon: "warning",
        text: resultado.mensagem,
      })
      return
    }

    const novoItemObj = {
      id: Date.now(), // ID único para controle
      nome: resultado.nome,
      quantidade: resultado.quantidade,
    }

    setItens([...itens, novoItemObj])
    setNovoItem("")
    setNovaQuantidade("")
  }

  const removerItem = id => {
    setItens(itens.filter(item => item.id !== id))
  }

  const validarFormulario = () => {
    const resultado = validarFormularioOS({
      condominioSelecionado,
      responsavelTecnico,
      tipoServico,
      descricao,
      assinatura,
      tiposServico,
    })

    if (!resultado.valido) {
      Swal.fire({
        icon: "warning",
        text: resultado.mensagem,
      })
      return false
    }

    return true
  }

  const salvarOS = async () => {
    if (!validarFormulario()) {
      return
    }

    try {
      // Criar descrição dos itens para DS_ITEM (se houver)
      const itensDescricao =
        itens.length > 0
          ? itens.map(i => `${i.nome} (${i.quantidade})`).join(", ")
          : ""

      // 1. Criar a OS - usando endpoint conforme Postman
      const dadosOS = {
        OID_EMPRESA: parseInt(condominioSelecionado),
        TIPO_ORDEM: parseInt(tipoServico),
        DS_ORDEM: descricao,
        DS_ITEM: itensDescricao,
        RESPONSAVEL: responsavelTecnico,
        STATUS: status, // Status no campo STATUS
        PROTOCOLO: protocolo, // Protocolo no campo PROTOCOLO
      }

      console.log("Criando OS com dados:", dadosOS)

      // Criar OS via API
      const responseOS = await api.post("/os", dadosOS)

      // A API retorna [[{"OID_OS":X}],Y] ou [{"OID_OS":X}]
      let osId
      if (Array.isArray(responseOS.data)) {
        if (Array.isArray(responseOS.data[0])) {
          osId = responseOS.data[0][0]?.OID_OS
        } else {
          osId = responseOS.data[0]?.OID_OS
        }
      } else {
        osId = responseOS.data?.OID_OS || responseOS.data?.id
      }

      console.log("OS criada com ID:", osId)

      if (!osId) {
        throw new Error("Não foi possível obter o ID da OS criada")
      }

      // 2. Enviar assinatura como anexo (se existir)
      if (assinatura) {
        const assinaturaBlob = await fetch(assinatura).then(res => res.blob())
        const assinaturaFile = new File(
          [assinaturaBlob],
          `assinatura_os_${osId}.png`,
          { type: "image/png" },
        )

        const formDataAssinatura = new FormData()
        formDataAssinatura.append("OID_OS", osId)
        formDataAssinatura.append("file", assinaturaFile)
        formDataAssinatura.append("DS_ANEXO", "Assinatura do Cliente")
        formDataAssinatura.append("RESPONSAVEL", responsavelTecnico)

        console.log("Enviando assinatura como anexo...")

        await api.post("/os_anexo", formDataAssinatura, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
      }

      // 3. Enviar cada item como anexo separado (com DS_ITEM e QTD_ITEM) - se houver itens
      if (itens.length > 0) {
        for (const item of itens) {
          // Criar um arquivo vazio para o item (API requer o campo file)
          const emptyBlob = new Blob([""], { type: "text/plain" })
          const emptyFile = new File([emptyBlob], `item_${item.id}.txt`, {
            type: "text/plain",
          })

          const formDataItem = new FormData()
          formDataItem.append("OID_OS", osId)
          formDataItem.append("file", emptyFile)
          formDataItem.append("DS_ANEXO", `Item: ${item.nome}`)
          formDataItem.append("DS_ITEM", item.nome)
          formDataItem.append("QTD_ITEM", item.quantidade.toString())
          formDataItem.append("RESPONSAVEL", responsavelTecnico)

          await api.post("/os_anexo", formDataItem, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
        }
      }

      // 5. Sucesso
      const tipoNome =
        tiposServico.find(t => t.OID_TIPO === parseInt(tipoServico))?.NAME ||
        tipoServico

      const condominioNome =
        condominios.find(
          c =>
            c.OID_EMPRESA === parseInt(condominioSelecionado) ||
            String(c.OID_EMPRESA) === String(condominioSelecionado),
        )?.NM_RAZAO_SOCIAL || ""

      const result = await Swal.fire({
        icon: "success",
        title: "Ordem de Serviço criada com sucesso!",
        html: `
          <div style="text-align: left; margin-top: 20px;">
            <p><strong>Protocolo:</strong> ${protocolo}</p>
            <p><strong>Data:</strong> ${formatarDataBR(dataAbertura)}</p>
            <p><strong>Tipo:</strong> ${tipoNome}</p>
            <p><strong>Status:</strong> ${mapearStatusLabel(status)}</p>
            ${itens.length > 0 ? `<p><strong>Itens:</strong> ${itens.length}</p>` : ""}
          </div>
        `,
        showDenyButton: true,
        confirmButtonText: "Salvar PDF",
        denyButtonText: "Ir para listagem",
        allowOutsideClick: false,
      })

      if (result.isConfirmed) {
        gerarPdfOS({
          protocolo,
          data: formatarDataBR(dataAbertura),
          condominio: condominioNome,
          responsavel: responsavelTecnico,
          tipo: tipoNome,
          status: mapearStatusLabel(status),
          descricao,
          itens: itens.map(i => ({ nome: i.nome, quantidade: i.quantidade })),
          assinatura: assinatura || null,
        })
      }

      navigate("/ordem-servico")
    } catch (error) {
      console.error("Erro ao salvar OS:", error)
      console.error("Detalhes do erro:", error.response?.data)

      let mensagemErro =
        "Ocorreu um erro ao criar a ordem de serviço. Tente novamente."

      if (error.message) {
        mensagemErro = error.message
      } else if (error.response?.data?.message) {
        mensagemErro = error.response.data.message
      } else if (error.response?.data) {
        mensagemErro = JSON.stringify(error.response.data)
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao criar Ordem de Serviço",
        text: mensagemErro,
      })
    }
  }

  const cancelar = () => {
    Swal.fire({
      title: "Deseja cancelar?",
      text: "Todos os dados serão perdidos",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, cancelar",
      cancelButtonText: "Não",
    }).then(result => {
      if (result.isConfirmed) {
        navigate("/ordem-servico")
      }
    })
  }

  // Opções para os selects (react-select) — derivadas dos dados carregados.
  // O estado continua guardando o ID (string), preservando a lógica de salvamento.
  const condominioOptions = condominios.map(c => ({
    value: c.OID_EMPRESA,
    label: c.NM_RAZAO_SOCIAL,
  }))
  const tipoOptions = tiposServico
    .filter(t => t.NAME && t.NAME.trim())
    .map(t => ({
      value: t.OID_TIPO,
      label: t.NAME,
    }))

  // Assinatura visível para todos os tipos; obrigatória só em Entrega de Equipamentos
  const tipoSelecionadoAtual = tiposServico.find(
    t => t.OID_TIPO === parseInt(tipoServico),
  )
  const assinaturaObrigatoria = tipoSelecionadoAtual?.NAME === TIPO_ENTREGA

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col md={12}>
              <Card>
                <CardBody>
                  {/* Cabeçalho */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                    <div className="d-flex align-items-center">
                      <Button
                        color="link"
                        className="p-0 me-3"
                        onClick={cancelar}
                      >
                        <i className="bx bx-arrow-back font-size-24"></i>
                      </Button>
                      <h4 className="card-title mb-0">Nova Ordem de Serviço</h4>
                    </div>
                  </div>

                  {/* Informações Principais */}
                  <Row>
                    <Col md={4}>
                      <label className="form-label">Protocolo</label>
                      <input
                        type="text"
                        value={protocolo}
                        disabled
                        className="form-control"
                      />
                    </Col>
                    <Col md={4}>
                      <label className="form-label">Data de Abertura</label>
                      <input
                        type="text"
                        value={formatarDataBR(dataAbertura)}
                        disabled
                        className="form-control"
                      />
                    </Col>
                    <Col md={4}>
                      <label className="form-label">Status</label>
                      <input
                        type="text"
                        value="Em aberto"
                        disabled
                        className="form-control"
                      />
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  {/* Dados do Serviço */}
                  <Row>
                    <Col md={6}>
                      <label className="form-label">
                        Condomínio <span className="text-danger">*</span>
                      </label>
                      <Select
                        placeholder="Selecione um condomínio"
                        options={condominioOptions}
                        value={
                          condominioOptions.find(
                            o =>
                              String(o.value) === String(condominioSelecionado),
                          ) || null
                        }
                        onChange={opt =>
                          setCondominioSelecionado(opt ? String(opt.value) : "")
                        }
                        isClearable
                        styles={selectMenuStyles}
                      />
                    </Col>
                    <Col md={6}>
                      <label className="form-label">
                        Responsável Técnico{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Nome do responsável técnico"
                        value={responsavelTecnico}
                        onChange={e => setResponsavelTecnico(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={8}>
                      <label className="form-label">
                        Tipo de Serviço <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex gap-2">
                        <div style={{ flex: 1 }}>
                          <Select
                            placeholder="Selecione o tipo de serviço"
                            options={tipoOptions}
                            value={
                              tipoOptions.find(
                                o => String(o.value) === String(tipoServico),
                              ) || null
                            }
                            onChange={opt =>
                              setTipoServico(opt ? String(opt.value) : "")
                            }
                            isClearable
                            styles={selectMenuStyles}
                          />
                        </div>
                        <Button
                          color="primary"
                          onClick={toggleModalNovoTipo}
                          title="Criar novo tipo de serviço"
                        >
                          <i className="bx bx-plus"></i>
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mt-2">
                    <Col md={12}>
                      <label className="form-label">
                        Descrição <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows="5"
                        placeholder="Descreva detalhadamente o serviço a ser realizado..."
                        value={descricao}
                        onChange={e => setDescricao(e.target.value)}
                        style={{ resize: "vertical" }}
                      />
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  {/* Seção de Itens */}
                  <div className="d-flex align-items-center mb-3">
                    <i className="bx bx-package font-size-24 text-primary me-2"></i>
                    <h5 className="mb-0">Itens</h5>
                  </div>

                  <Row className="align-items-end">
                    <Col md={5}>
                      <label className="form-label">Nome do Item</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ex: Sensor de temperatura"
                        value={novoItem}
                        onChange={e => setNovoItem(e.target.value)}
                      />
                    </Col>
                    <Col md={3} className="mt-2 mt-md-0">
                      <label className="form-label">Quantidade</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Ex: 5"
                        min="1"
                        value={novaQuantidade}
                        onChange={e => setNovaQuantidade(e.target.value)}
                      />
                    </Col>
                    <Col md={4} className="mt-2 mt-md-0">
                      <Button
                        color="success"
                        className="w-100"
                        onClick={adicionarItem}
                      >
                        <i className="bx bx-plus me-1"></i>
                        Adicionar Item
                      </Button>
                    </Col>
                  </Row>

                  {/* Lista de itens adicionados */}
                  {itens.length > 0 ? (
                    <div className="table-responsive mt-3">
                      <Table className="table align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "50px" }}>#</th>
                            <th>Item</th>
                            <th className="text-center" style={{ width: "120px" }}>
                              Quantidade
                            </th>
                            <th className="text-center" style={{ width: "80px" }}>
                              Ação
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {itens.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.nome}</td>
                              <td className="text-center">
                                <span className="badge bg-primary">
                                  {item.quantidade}
                                </span>
                              </td>
                              <td className="text-center">
                                <BotaoAcao
                                  iconClassName="bx bx-trash align-middle text-danger"
                                  onClick={() => removerItem(item.id)}
                                  title="Remover item"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4 border rounded bg-light mt-3">
                      <i className="bx bx-package font-size-24 mb-2 d-block opacity-50"></i>
                      Nenhum item adicionado
                    </div>
                  )}

                  {/* Assinatura Digital — visível para todos; obrigatória só em Entrega */}
                  <>
                      <hr className="my-4" />
                      <div className="d-flex align-items-center mb-3">
                        <i className="bx bx-edit-alt font-size-24 text-primary me-2"></i>
                        <h5 className="mb-0">
                          Assinatura do Cliente{" "}
                          {assinaturaObrigatoria && (
                            <span className="text-danger">*</span>
                          )}
                        </h5>
                      </div>
                      {assinatura ? (
                        <div className="text-center">
                          <div className="border rounded p-3 d-inline-block bg-white">
                            <div className="mb-3">
                              <i className="bx bxs-check-circle text-success font-size-36"></i>
                              <h6 className="text-success mt-2 mb-3 fw-bold">
                                Assinatura capturada com sucesso
                              </h6>
                            </div>
                            <img
                              src={assinatura}
                              alt="Assinatura"
                              className="img-fluid"
                            />
                            <div className="mt-4">
                              <Button
                                color="warning"
                                onClick={toggleModalAssinatura}
                              >
                                <i className="bx bx-refresh me-1"></i>
                                Refazer Assinatura
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-5 border rounded bg-light">
                          <i className="bx bx-pen font-size-48 text-muted mb-4 d-block"></i>
                          <h6 className="text-muted mb-4">
                            Clique no botão abaixo para capturar a assinatura do
                            cliente
                            {!assinaturaObrigatoria && (
                              <span className="d-block mt-1 small">
                                (opcional para este tipo de serviço)
                              </span>
                            )}
                          </h6>
                          <Button color="primary" onClick={toggleModalAssinatura}>
                            <i className="bx bx-edit-alt me-1"></i>
                            Assinar Ordem de Serviço
                          </Button>
                        </div>
                      )}
                  </>

                  <hr className="my-4" />

                  {/* Botões de Ação */}
                  <div className="d-flex justify-content-end flex-wrap gap-2">
                    <Button color="light" onClick={limparFormulario}>
                      <i className="bx bx-refresh me-1"></i> Limpar
                    </Button>
                    <Button color="danger" onClick={cancelar}>
                      <i className="bx bx-x me-1"></i> Cancelar
                    </Button>
                    <Button color="success" onClick={salvarOS}>
                      <i className="bx bx-save me-1"></i> Salvar OS
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal de Assinatura */}
      <ModalFormOS
        isOpen={modalAssinatura}
        toggle={toggleModalAssinatura}
        size="xl"
        title="Assinatura Digital"
        footer={
          <>
            <Button color="warning" onClick={limparAssinatura}>
              <i className="bx bx-eraser me-1"></i> Limpar
            </Button>
            <Button color="secondary" onClick={toggleModalAssinatura}>
              Cancelar
            </Button>
            <Button color="success" onClick={salvarAssinatura}>
              <i className="bx bx-check me-1"></i> Confirmar
            </Button>
          </>
        }
      >
          <div className="text-center">
            <h5 className="mb-4 font-size-18">
              <i className="bx bx-info-circle text-primary me-2"></i>
              Por favor, assine no campo abaixo
            </h5>
            <div
              ref={signatureContainerRef}
              className="border rounded bg-white mx-auto"
              style={{ width: "100%", maxWidth: 900 }}
            >
              <SignatureCanvas
                key={`${canvasSize.width}x${canvasSize.height}`}
                ref={signatureRef}
                canvasProps={{
                  width: canvasSize.width,
                  height: canvasSize.height,
                  className: "signature-canvas",
                  style: {
                    width: `${canvasSize.width}px`,
                    height: `${canvasSize.height}px`,
                    touchAction: "none",
                    display: "block",
                  },
                }}
                backgroundColor="white"
              />
            </div>
            <p className="text-muted mt-4 mb-0 font-size-14">
              <i className="bx bx-info-circle me-1"></i>
              Use o mouse, dedo ou caneta stylus para assinar
            </p>
          </div>
      </ModalFormOS>

      {/* Modal de Novo Tipo de Serviço */}
      <TipoServicoModal
        isOpen={modalNovoTipo}
        toggle={toggleModalNovoTipo}
        valor={novoTipoServico}
        onChange={setNovoTipoServico}
        onSalvar={salvarNovoTipo}
        tipos={tiposServico}
        onEditarTipo={editarTipoServico}
        onExcluirTipo={excluirTipoServico}
      />
    </React.Fragment>
  )
}

export default CriarOS
